import { Download, Loader2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Constants } from "../../constant";
import {
  ActivityDomain,
  ActivityDomains,
  ActivityEntry,
  fetchActivityPage,
  fetchAllActivityEntries,
  getActivityDomainLabel,
} from "../../lib/activityService";
import { hasPermission, Permission } from "../../lib/roles";
import { selectUserProfile } from "../../store/authSlice";
import DuoSelect from "../DuoSelect";
import ActivityEntryListItem from "./ActivityEntry";
import { SettingsHeader } from "./SettingsThemesSection";
/** One-time RTDB reads per page (smaller than loading the full list at once). */
const PAGE_SIZE = 30;

export const ACTION_STYLES: Record<string, string> = {
  create: "bg-emerald-500/20 text-emerald-300 border-emerald-400/30",
  update: "bg-blue-500/20 text-blue-300 border-blue-400/30",
  delete: "bg-red-500/20 text-red-300 border-red-400/30",
  toggle: "bg-amber-500/20 text-amber-300 border-amber-400/30",
  publish: "bg-teal-500/20 text-teal-300 border-teal-400/30",
  archive: "bg-rose-500/20 text-rose-300 border-rose-400/30",
  reorder: "bg-indigo-500/20 text-indigo-300 border-indigo-400/30",
  mark_for_deletion: "bg-rose-500/20 text-rose-300 border-rose-400/30",
};

const domainOptions: {
  value: "all" | ActivityDomain;
  label: string;
}[] = [
  { value: "all", label: "All areas" },
  {
    value: ActivityDomains.FeatureFlags,
    label: getActivityDomainLabel(ActivityDomains.FeatureFlags),
  },
  {
    value: ActivityDomains.Users,
    label: getActivityDomainLabel(ActivityDomains.Users),
  },
  {
    value: ActivityDomains.VersionControl,
    label: getActivityDomainLabel(ActivityDomains.VersionControl),
  },
  {
    value: ActivityDomains.DynamicLinks,
    label: getActivityDomainLabel(ActivityDomains.DynamicLinks),
  },
  {
    value: ActivityDomains.HappeningNow,
    label: getActivityDomainLabel(ActivityDomains.HappeningNow),
  },
  {
    value: ActivityDomains.Broadcasts,
    label: getActivityDomainLabel(ActivityDomains.Broadcasts),
  },
  {
    value: ActivityDomains.PortalNotifications,
    label: getActivityDomainLabel(ActivityDomains.PortalNotifications),
  },
  {
    value: ActivityDomains.ImageHosting,
    label: getActivityDomainLabel(ActivityDomains.ImageHosting),
  },
];

interface SettingsActivitySectionProps {
  isActive?: boolean;
  refreshKey?: number;
}

const SettingsActivitySection: React.FC<SettingsActivitySectionProps> = ({
  isActive = false,
  refreshKey = 0,
}) => {
  const userProfile = useSelector(selectUserProfile);

  const [entries, setEntries] = useState<ActivityEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [domainFilter, setDomainFilter] = useState<"all" | ActivityDomain>(
    "all",
  );
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState<number | null>(null);
  const [exportNotice, setExportNotice] = useState<string | null>(null);

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const loadMoreTriggerRef = useRef<HTMLDivElement | null>(null);

  const canViewActivity =
    userProfile != null &&
    hasPermission(userProfile.role, Permission.VIEW_ACTIVITY);

  const canExportActivity =
    userProfile != null &&
    hasPermission(userProfile.role, Permission.EXPORT_ACTIVITY);

  const loadFirstPage = useCallback(async () => {
    if (!canViewActivity) return;

    setLoading(true);
    setError(null);

    try {
      const rows = await fetchActivityPage(PAGE_SIZE, null);

      setEntries(rows);
      setHasMore(rows.length === PAGE_SIZE);
      setExpandedId(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load activity");
    } finally {
      setLoading(false);
    }
  }, [canViewActivity]);

  const loadMore = useCallback(async () => {
    if (
      !canViewActivity ||
      loading ||
      loadingMore ||
      !hasMore ||
      entries.length === 0
    ) {
      return;
    }

    const oldest = entries[entries.length - 1];

    setLoadingMore(true);
    setError(null);

    try {
      const next = await fetchActivityPage(PAGE_SIZE, {
        ts: oldest.ts,
        id: oldest.id,
      });

      setEntries((prev) => {
        const seen = new Set(prev.map((entry) => entry.id));
        const uniqueNext = next.filter((entry) => !seen.has(entry.id));

        return uniqueNext.length > 0 ? [...prev, ...uniqueNext] : prev;
      });

      setHasMore(next.length === PAGE_SIZE);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load more activity");
    } finally {
      setLoadingMore(false);
    }
  }, [canViewActivity, entries, hasMore, loading, loadingMore]);

  useEffect(() => {
    if (!isActive) return;

    void loadFirstPage();
  }, [isActive, refreshKey, loadFirstPage]);

  useEffect(() => {
    if (!isActive) return;

    const container = scrollContainerRef.current;
    const trigger = loadMoreTriggerRef.current;

    if (!container || !trigger) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          void loadMore();
        }
      },
      {
        root: container,
        rootMargin: "160px",
        threshold: 0,
      },
    );

    observer.observe(trigger);

    return () => observer.disconnect();
  }, [isActive, loadMore]);

  const filteredEntries = useMemo(() => {
    if (domainFilter === "all") return entries;

    return entries.filter((entry) => entry.domain === domainFilter);
  }, [entries, domainFilter]);

  const handleExportAll = useCallback(async () => {
    if (!canExportActivity || exporting) return;

    setExporting(true);
    setExportProgress(0);
    setError(null);
    setExportNotice(null);

    try {
      const { entries: allRows, capped } = await fetchAllActivityEntries({
        onProgress: (count) => setExportProgress(count),
      });

      const payload = {
        exportedAt: new Date().toISOString(),
        rowCount: allRows.length,
        truncated: capped,
        activity: allRows,
      };

      const blob = new Blob([JSON.stringify(payload, null, 2)], {
        type: "application/json;charset=utf-8",
      });

      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 19);

      anchor.href = url;
      anchor.download = `live-presence-activity-${stamp}.json`;
      anchor.rel = "noopener";

      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();

      URL.revokeObjectURL(url);

      setExportNotice(
        capped
          ? `Saved ${allRows.length.toLocaleString()} rows. Safety limit reached, so older events may not be included.`
          : `Downloaded ${allRows.length.toLocaleString()} event${
              allRows.length === 1 ? "" : "s"
            }.`,
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : "Export failed");
    } finally {
      setExporting(false);
      setExportProgress(null);
    }
  }, [canExportActivity, exporting]);

  if (!canViewActivity) {
    return (
      <section className="flex h-full min-h-0 flex-col">
        <SettingsHeader
          title="Activity"
          description={`Audit log of dashboard changes. Entries older than ${Constants.ACTIVITY_RETENTION_DAYS} days are removed automatically.`}
        />

        <div className="mt-3 rounded-2xl border border-white/10 bg-gray-950/50 p-5 text-center">
          <p className="font-extrabold text-white">Access denied</p>
          <p className="mt-1 text-sm text-gray-400">
            You do not have permission to view activity.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="flex h-full min-h-0 flex-col gap-3">
      <header className="shrink-0 rounded-2xl border border-white/10 bg-gray-950/90 p-4 shadow-sm backdrop-blur-sm">
        <div className="flex items-start justify-between gap-3">
          <h2 className="text-lg font-extrabold tracking-tight text-white">
            Activity
          </h2>
          <p className="shrink-0 rounded-full border border-white/10 bg-white/5 px-2.5 py-1 text-xs font-bold text-gray-300">
            Last {Constants.ACTIVITY_RETENTION_DAYS} days
          </p>
        </div>
        <p className="mt-1 max-w-2xl text-sm leading-5 text-gray-400">
          Audit log of dashboard changes. Kept for{" "}
          {Constants.ACTIVITY_RETENTION_DAYS} days; older entries are purged
          daily. Export if you need a longer archive.
        </p>
      </header>

      <div className="flex shrink-0 flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <label className="min-w-0 sm:w-56">
          <span className="sr-only">Filter by area</span>
          <DuoSelect
            value={domainFilter}
            onChange={setDomainFilter}
            options={domainOptions}
            label="Area"
            className="w-full sm:w-56 border-white/10 bg-gray-150"
          />
        </label>

        {canExportActivity && (
          <button
            type="button"
            onClick={() => void handleExportAll()}
            disabled={exporting}
            className="inline-flex h-10 items-center justify-center gap-2 rounded-xl border border-emerald-400/30 bg-emerald-400/15 px-3 text-sm font-extrabold text-emerald-100 transition hover:bg-emerald-400/25 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {exporting ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
            ) : (
              <Download className="h-4 w-4" aria-hidden />
            )}

            {exporting && exportProgress != null
              ? `${exportProgress.toLocaleString()} rows`
              : "Export"}
          </button>
        )}
      </div>

      {error && (
        <div
          className="shrink-0 rounded-xl border border-red-400/30 bg-red-500/15 px-3 py-2 text-sm text-red-100"
          role="alert"
        >
          {error}
        </div>
      )}

      {exportNotice && (
        <div className="flex shrink-0 items-start justify-between gap-3 rounded-xl border border-amber-400/30 bg-amber-400/10 px-3 py-2 text-sm text-amber-100">
          <p>{exportNotice}</p>

          <button
            type="button"
            onClick={() => setExportNotice(null)}
            className="shrink-0 font-bold text-amber-100/80 hover:text-amber-100"
          >
            Dismiss
          </button>
        </div>
      )}

      <div
        ref={scrollContainerRef}
        className="min-h-0 flex-1 overflow-y-auto overscroll-contain pr-1"
      >
        {loading && entries.length === 0 && !error ? (
          <div className="flex h-full min-h-[14rem] items-center justify-center gap-3 text-sm text-gray-400">
            <Loader2 className="h-6 w-6 animate-spin" aria-hidden />
            Loading activity…
          </div>
        ) : filteredEntries.length === 0 ? (
          <div className="rounded-2xl border border-white/10 bg-white/5 px-5 py-10 text-center text-sm text-gray-400">
            {entries.length === 0
              ? "No activity recorded yet."
              : "No loaded events match this filter."}
          </div>
        ) : (
          <ul className="space-y-2 mr-2">
            {filteredEntries.map((entry) => (
              <ActivityEntryListItem
                key={entry.id}
                entry={entry}
                expanded={expandedId === entry.id}
                onToggle={() =>
                  setExpandedId((currentId) =>
                    currentId === entry.id ? null : entry.id,
                  )
                }
              />
            ))}

            <li>
              <div
                ref={loadMoreTriggerRef}
                className="flex h-12 items-center justify-center text-xs text-gray-500"
              >
                {loadingMore && (
                  <span className="inline-flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                    Loading older events…
                  </span>
                )}

                {!loadingMore && !hasMore && entries.length > 0 && (
                  <span>End of activity</span>
                )}
              </div>
            </li>
          </ul>
        )}
      </div>
    </section>
  );
};

export default SettingsActivitySection;
